import { Vue } from "vue-class-component";
import { ElMessage, ElMessageBox } from "element-plus";
import { BaseEntity, MainEntity } from "../base/IBaseEntity";
import { QueryParam } from "../service/DataService";
import { ServiceContext, ServiceData, ServiceFactory } from "../base/ServiceData";
import { BaseService } from "../service/BaseService";
import { MenuData } from "../base/MenuData";
import bus from "@/script/core/util/Bus";
import { DictService } from "@/script/apps/system/service/DictService";
import { Dict, Permission } from "@/script/apps/system/entity/SystemEntity";
import { DictUtil } from "../Dict";
import store from "@/store";
import { AuthService } from "@/script/apps/system/service/AuthService";

export abstract class BaseList extends Vue {
  public tableHeight?: number;

  public states: Dict[] = [];

  public types: Dict[] = [];

  public auths = new Map<string, boolean>();

  public pageSizes = [10, 20, 50, 100, 200, 500, 1000];

  public serviceContext: ServiceContext = ServiceFactory.build(
    "",
    "",
    "",
    new BaseService()
  );

  public queryParam: QueryParam = {
    pager: { pageIndex: 1, pageSize: 20, total: 0, sorters: [] },
    entity: null,
    extendMap: {}
  };

  public selectedList: BaseEntity[] = [];

  beforeCreate() {
    const head = 50;
    const footer = 40;
    const filter = 62;
    const tool = 54;
    const pager = 34;
    const tabs = 56;
    this.tableHeight =
      document.body.clientHeight -
      head -
      footer -
      filter -
      tool -
      pager -
      tabs -
      40;
  }

  created() {
    this.queryParam.entity = this.initQueryEntity();
    this.serviceContext = this.initData();
    this.initOptions();
  }

  activated() {
    this.initAuth();
    this.initParam();
    this.loadList();
  }

  headerCellClass({ row, column, rowIndex, columnIndex }: any) {
    return "list-table-header-cell";
  }

  protected async initAuth() {
    this.auths.set("add", false);
    this.auths.set("view", false);
    this.auths.set("modify", false);
    this.auths.set("delete", false);

    const authService = new AuthService();
    await authService.loadUserPermissionList();
    const permissions = store.getters.loginUserPermissions;
    permissions.forEach((item: Permission) => {
      if (item.code === this.serviceContext.serviceData.billName) {
        const billId = item.id;
        permissions.forEach((item2: Permission) => {
          if (billId === item2.parentId) {
            this.auths.set(item2.code!, true);
          }
        });
      }
    });
  }

  protected disableAuth(key: string) {
    return !this.auths.get(key);
  }

  protected initOptions() {
    const dictService = new DictService();
    if (this.serviceContext.statesName) {
      dictService.loadChildItems(this.serviceContext.statesName, (data: any) => {
        this.states = data;
      });
    }
    if (this.serviceContext.typesName) {
      dictService.loadChildItems(this.serviceContext.typesName, (data: any) => {
        this.types = data;
      });
    }
  }

  selectList() {
    this.loadList();
  }

  resetSelect() {
    this.queryParam.entity = this.initQueryEntity();
    this.loadList();
  }

  handleSizeChange(val: any) {
    this.loadList();
  }

  handleCurrentChange(val: any) {
    this.loadList();
  }

  handleSelectionChange(val: any) {
    this.selectedList = val;
  }

  sortChange(column: any) {
    this.queryParam.pager?.sorters.forEach((item) => {
      if (item.prop === column.prop) {
        if (column.order === "descending") {
          item.sortMode = "desc";
        } else {
          item.sortMode = "asc";
        }
      }
    });
    this.loadList();
  }

  public loadList() {
    const self = this;
    this.beforeLoadList();
    self.serviceContext.service.loadList(
      this.queryParam,
      function (data: any) {
        // 包装列表数据
        self.wrapList(self, data);
        self.afterLoadList(data);
      },
      function (error: Error) {
        ElMessage.error("加载列表失败，失败原因：" + error.message);
      },
      self.serviceContext.serviceData.loadListApiName
    );
  }

  public add() {
    this.$router.push({
      path: this.serviceContext.detailUrl,
      query: { id: 0, type: "A", cache: "0" }
    });
    const menuData: MenuData = {
      name: String(this.serviceContext.listUrl),
      path: String(this.serviceContext.detailUrl),
      query: { type: "A", cache: "1" },
      isClose: true
    };
    bus.emit("to-detail", menuData);
  }

  public view(scope: any) {
    const id = scope.row.id;
    this.$router.push({
      path: this.serviceContext.detailUrl,
      query: { id: id, type: "V", cache: "0" }
    });
    const menuData: MenuData = {
      name: String(this.serviceContext.listUrl),
      path: String(this.serviceContext.detailUrl),
      query: { id: id, type: "V", cache: "1" },
      isClose: true
    };
    bus.emit("to-detail", menuData);
  }

  public modify(scope: any) {
    const id = scope.row.id;
    this.$router.push({
      path: this.serviceContext.detailUrl,
      query: { id: id, type: "M", cache: "0" }
    });
    const menuData: MenuData = {
      name: String(this.serviceContext.listUrl),
      path: String(this.serviceContext.detailUrl),
      query: { id: id, type: "M", cache: "1" },
      isClose: true
    };
    bus.emit("to-detail", menuData);
  }

  public deleteItem(scope: any) {
    const self = this;
    ElMessageBox.confirm("此操作将删除当前行的数据, 是否继续?", "提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning"
    })
      .then(() => {
        const deleteItem = scope.row as BaseEntity;
        const deleteItems: BaseEntity[] = [deleteItem];
        self.serviceContext.service.deleteList(
          self.serviceContext.serviceData.deleteListApiName,
          deleteItems,
          function (data: any) {
            self.loadList();
            ElMessage({ type: "success", message: "删除成功!" });
          },
          function (error: Error) {
            ElMessage.error("删除失败，失败原因：" + error.message);
          }
        );
      })
      .catch(() => {
        ElMessage({
          type: "info",
          message: "已取消删除"
        });
      });
  }

  public deleteList() {
    if (this.selectedList.length === 0) {
      ElMessage.error("请选择要删除的数据行！");
      return;
    }
    const self = this;
    ElMessageBox.confirm("此操作将删除选中的数据, 是否继续?", "提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning"
    })
      .then(() => {
        self.serviceContext.service.deleteList(
          self.serviceContext.serviceData.deleteListApiName,
          self.selectedList,
          function (data: any) {
            self.loadList();
            ElMessage({ type: "success", message: "删除成功!" });
          },
          function (error: Error) {
            ElMessage.error("删除失败，失败原因：" + error.message);
          }
        );
      })
      .catch(() => {
        ElMessage({
          type: "info",
          message: "已取消删除"
        });
      });
  }

  public exportList() {
    ElMessage.error("暂时未实现功能！");
  }

  public wrapList(context: BaseList, data: any) {
    context.queryParam.pager!.total = data.total;

    const dataList: BaseEntity[] = data.list as BaseEntity[];
    let no =
      (context.queryParam.pager!.pageIndex - 1) *
      context.queryParam.pager!.pageSize +
      1;
    dataList.forEach(item => {
      item.no = no++;
      if (context.serviceContext.statesName) {
        const mitem = item as MainEntity;
        mitem.stateName = DictUtil.getItemName(context.states, mitem.state!);
      }
    });
  }

  public abstract initData(): ServiceContext;
  public abstract initQueryEntity(): MainEntity;
  public initParam() { }
  public beforeLoadList() { }
  public abstract afterLoadList(data: any): void;
}

